import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  OfficialDocument,
  OfficialDocumentAttachment,
  OfficialDocumentDirection,
  OfficialDocumentPayload,
} from '../types/officialDocument';

export async function fetchOfficialDocuments(
  keyword?: string,
  direction?: OfficialDocumentDirection | '' | null,
  contractId?: number | null
): Promise<OfficialDocument[]> {
  const params: Record<string, string | number> = {};
  if (keyword?.trim()) params.q = keyword.trim();
  if (direction === 'INCOMING' || direction === 'OUTGOING') params.direction = direction;
  if (contractId != null) params.contractId = contractId;
  const { data } = await apiClient.get<OfficialDocument[]>('/official-documents', {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function fetchOfficialDocument(
  officialDocumentId: number
): Promise<OfficialDocument> {
  const { data } = await apiClient.get<OfficialDocument>(
    `/official-documents/${officialDocumentId}`
  );
  return data;
}

export async function createOfficialDocument(
  payload: OfficialDocumentPayload
): Promise<OfficialDocument> {
  const { data } = await apiClient.post<OfficialDocument>('/official-documents', payload);
  return data;
}

export async function updateOfficialDocument(
  officialDocumentId: number,
  payload: OfficialDocumentPayload
): Promise<OfficialDocument> {
  const { data } = await apiClient.patch<OfficialDocument>(
    `/official-documents/${officialDocumentId}`,
    payload
  );
  return data;
}

export async function deleteOfficialDocument(officialDocumentId: number): Promise<void> {
  await apiClient.delete(`/official-documents/${officialDocumentId}`);
}

export async function approveOfficialDocument(
  officialDocumentId: number
): Promise<OfficialDocument> {
  const { data } = await apiClient.post<OfficialDocument>(
    `/official-documents/${officialDocumentId}/approve`
  );
  return data;
}

export async function revokeOfficialDocument(
  officialDocumentId: number
): Promise<OfficialDocument> {
  const { data } = await apiClient.post<OfficialDocument>(
    `/official-documents/${officialDocumentId}/revoke`
  );
  return data;
}

export async function uploadOfficialDocumentAttachments(
  officialDocumentId: number,
  files: File[]
): Promise<OfficialDocumentAttachment[]> {
  const form = new FormData();
  files.forEach((file) => form.append('files', file));
  const { data } = await apiClient.post<OfficialDocumentAttachment[]>(
    `/official-documents/${officialDocumentId}/attachments`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  return data;
}

export async function deleteOfficialDocumentAttachment(
  officialDocumentId: number,
  attachmentId: number
): Promise<void> {
  await apiClient.delete(
    `/official-documents/${officialDocumentId}/attachments/${attachmentId}`
  );
}

export async function downloadOfficialDocumentAttachment(
  officialDocumentId: number,
  attachmentId: number,
  fileName: string
): Promise<void> {
  const { data } = await apiClient.get<Blob>(
    `/official-documents/${officialDocumentId}/attachments/${attachmentId}/download`,
    { responseType: 'blob' }
  );
  const url = window.URL.createObjectURL(data);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  window.URL.revokeObjectURL(url);
}


export async function downloadOfficialDocumentPdf(
  officialDocumentId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/official-documents/${officialDocumentId}/pdf`,
    fileName || `공문서_${officialDocumentId}.pdf`
  );
}
